/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useState } from 'react'
import { AsyncThunk } from '@reduxjs/toolkit/dist/createAsyncThunk'
import dayjs from 'dayjs'
import { uniq } from 'ramda'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  AddIcon,
  CancelIcon,
  CertificateIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  PdfIcon,
  SearchIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import { useAppDispatch } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ExternalCertificate,
  LiveCertificate,
  RecordedCertificate,
  User,
} from 'src/v3/core/types'
import {
  ContentType,
  contentTypes,
  downloadFile,
  FileData,
} from 'src/v3/utilities/filesystem'
import ConfirmAction from 'src/v3/components/confirm-action'

type Domain = 'manage' | 'profile'
type SortOption =
  | 'name-az'
  | 'name-za'
  | 'date-new'
  | 'date-old'
  | 'hours-long'
  | 'hours-short'

let getCertificate = (id: string, type: string, domain: Domain) =>
  axios
    .get(
      `/api/v2/${domain}/certificate/export?certificateID=${id}&certificateType=${type}`,
    )
    .then(pluckData)

let updateExternalCertificateImage = (
  data: {
    id: string
    imageType: string
  },
  domain: Domain,
) =>
  axios
    .put<string>(`/api/v2/${domain}/external-certificate-image`, data)
    .then(pluckData)

let getCertificatesExport = (id: string) =>
  axios
    .get<any>(`/api/v2/manage/certificates/export/user?userID=${id}`)
    .then(pluckData)

let deleteExternalCertificate = (id: string) =>
  axios.delete<unknown>(
    `/api/v2/manage/delete-external-certificate?CertificateID=${id}`,
  )

function prepareCertificates<
  T extends {
    name: string
    dateCompleted: string
  },
>(
  certificates: T[],
  certificateSearch: string,
  certificateSort: SortOption,
  dateLowerFilter: any,
  dateUpperFilter: any,
): T[] {
  return (
    certificates
      .filter((c) =>
        c.name.toLowerCase().includes(certificateSearch.toLowerCase()),
      )
      // filter by date range
      .filter((c) => {
        if (!dateLowerFilter && !dateUpperFilter) return true
        let date = dayjs(c.dateCompleted)
        return date.isAfter(dateLowerFilter) && date.isBefore(dateUpperFilter)
      })
      .sort((a, b) => {
        if (certificateSort === 'name-az') {
          return a.name.localeCompare(b.name || '') || 0
        }
        if (certificateSort === 'name-za') {
          return b.name.localeCompare(a.name || '') || 0
        }
        if (certificateSort === 'date-old') {
          return dayjs(a.dateCompleted).diff(b.dateCompleted)
        }
        if (certificateSort === 'hours-long') {
          return ((b as any).hours ?? 0) - ((a as any).hours ?? 0)
        }
        if (certificateSort === 'hours-short') {
          return ((a as any).hours ?? 0) - ((b as any).hours ?? 0)
        }
        return dayjs(b.dateCompleted).diff(a.dateCompleted)
      })
  )
}

export let CertificateList = ({
  certificates,
  certificateType,
  setErrorMessage,
  domain,
  user,
}: {
  certificates: (RecordedCertificate | LiveCertificate)[]
  certificateType: 'Recorded' | 'Live'
  setErrorMessage: (value: React.SetStateAction<string>) => void
  domain: Domain
  user?: User
}): ReactElement => {
  let [certificateSearch, setCertificateSearch] = useState<string>('')
  let [certificateSection, setCertificateSection] = useState<string>('')
  let [certificateSort, setCertificateSort] = useState<SortOption>('date-new')
  let [certificateTab, setCertificateTab] = useState<string>('current')
  let [downloading, setDownloading] = useState<string>('')
  let [dateLowerFilter, setDateLowerFilter] = useState<any>(dayjs('2000-01-01'))

  let [dateUpperFilter, setDateUpperFilter] = useState<any>(dayjs())

  let filteredCertificates = [...certificates]

  if (certificateTab === 'archive') {
    filteredCertificates = certificates.filter((cert) =>
      certificates.some(
        (c) =>
          c.name === cert.name &&
          dayjs(c.dateCompleted).isAfter(cert.dateCompleted),
      ),
    )
  } else if (certificateTab === 'current') {
    filteredCertificates = certificates.filter((cert) =>
      certificates.every(
        (c) =>
          c.name !== cert.name ||
          !dayjs(c.dateCompleted).isAfter(cert.dateCompleted),
      ),
    )
  }

  filteredCertificates = prepareCertificates(
    filteredCertificates,
    certificateSearch,
    certificateSort,
    dateLowerFilter,
    dateUpperFilter,
  )

  if (certificateType === 'Recorded' && certificateSection) {
    filteredCertificates = filteredCertificates.filter(
      (cert) => (cert as RecordedCertificate).section === certificateSection,
    )
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap-reverse', mt: '-6px' }}>
        <Tabs
          variant="scrollable"
          value={certificateTab}
          onChange={(e, num) => setCertificateTab(num)}
        >
          <Tab value="current" label="Current" />
          <Tab value="archive" label="Archive" />
          <Tab value="all" label="All" />
          {domain === 'manage' && <Divider orientation="vertical" flexItem />}
        </Tabs>
        {domain === 'manage' && (
          <Box style={{ display: 'flex', gap: 0.5, padding: '0 8px' }}>
            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              disabled={!filteredCertificates.length}
              onClick={() =>
                getCertificatesExport(user?.id || '')
                  .then((response) =>
                    downloadFile(
                      response,
                      `${user?.lastName}_certificates.csv`,
                    ),
                  )
                  .catch((err) =>
                    setErrorMessage(
                      `CSV Export Failed: ${err.response?.data || err}`,
                    ),
                  )
              }
            >
              Export
            </Button>
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
        <TextField
          sx={{ width: 240 }}
          variant="outlined"
          size="small"
          label={`Search ${certificateType} Certificates`}
          placeholder="Name"
          name={`${certificateType.toLowerCase()}-certificate-search`}
          type="text"
          value={certificateSearch}
          onChange={(event: React.ChangeEvent) => {
            setCertificateSearch((event.target as HTMLInputElement).value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {certificateType === 'Recorded' && (
          <FormControl sx={{ width: 160 }}>
            <InputLabel size="small">Category</InputLabel>
            <Select
              value={certificateSection}
              label="Category"
              variant="outlined"
              size="small"
              onChange={(event) => setCertificateSection(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {uniq(
                certificates.map(
                  (cert) => (cert as RecordedCertificate).section,
                ),
              )
                .sort((a, b) => b.localeCompare(a))
                .map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        <FormControl sx={{ width: 160 }}>
          <InputLabel size="small">Sort</InputLabel>
          <Select
            value={certificateSort}
            label="Sort"
            variant="outlined"
            size="small"
            onChange={(event) =>
              setCertificateSort(event.target.value as SortOption)
            }
          >
            <MenuItem value="date-new">Date: Newest</MenuItem>
            <MenuItem value="date-old">Date: Oldest</MenuItem>
            <MenuItem value="name-az">Name: A-Z</MenuItem>
            <MenuItem value="name-za">Name: Z-A</MenuItem>
            <MenuItem value="hours-long">Hours: Longest</MenuItem>
            <MenuItem value="hours-short">Hours: Shortest</MenuItem>
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: 160 }}
            label="From"
            value={dayjs(dateLowerFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) => setDateLowerFilter(newValue)}
          />
          <DatePicker
            sx={{ width: 160 }}
            label="To"
            value={dayjs(dateUpperFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) => setDateUpperFilter(newValue)}
          />
        </LocalizationProvider>
      </Box>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 15,
          marginBottom: 15,
          overflowY: 'scroll',
          maxHeight: 500,
        }}
      >
        {filteredCertificates.length ? (
          filteredCertificates.map((certificate) => (
            <Card
              key={certificate.id}
              variant="outlined"
              sx={{
                p: 2,
                width: 280,
                textAlign: 'center',
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="h6">
                  <CertificateIcon className="mb-1 mr-1" color="primary" />
                  {certificate.name}
                </Typography>
                {certificateType === 'Recorded' && (
                  <Typography color="text.secondary" variant="subtitle2">
                    {(certificate as RecordedCertificate).section}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography>
                  {certificate.hours} Hour{certificate.hours > 1 && 's'}
                </Typography>
                <Typography>
                  Completed{' '}
                  {dayjs(certificate.dateCompleted).format('MM/DD/YYYY')}
                </Typography>
              </Box>
              <Box>
                {downloading === certificate.id ? (
                  <CircularProgress size="24px" />
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PdfIcon />}
                    disabled={!certificate.fileName}
                    onClick={() => {
                      setDownloading(certificate.id)
                      getCertificate(
                        certificate.id,
                        certificateType.toLowerCase(),
                        domain,
                      )
                        .then((response) =>
                          axios
                            .get(response, { responseType: 'blob' })
                            .catch(() => {
                              // eslint-disable-next-line fp/no-throw, no-throw-literal
                              throw 'File Not Found'
                            }),
                        )
                        .then(pluckData)
                        .then((response) =>
                          downloadFile(
                            response,
                            `${certificate.name || certificate.id}.pdf`,
                          ),
                        )
                        .then(() => setDownloading(''))
                        .catch((err) => {
                          setDownloading('')
                          setErrorMessage(
                            `PDF Download Failed: ${err.response?.data || err}`,
                          )
                        })
                    }}
                  >
                    Download
                  </Button>
                )}
              </Box>
            </Card>
          ))
        ) : (
          <Typography color="text.secondary">
            There are no certificates matching these filters.
          </Typography>
        )}
      </div>
    </>
  )
}

export let ExternalCertificateList = ({
  certificates,
  fetchCertificates,
  setErrorMessage,
  config,
}: {
  certificates: ExternalCertificate[]
  fetchCertificates: AsyncThunk<void, void, object>
  setErrorMessage: (value: React.SetStateAction<string>) => void
  config?: {
    userID: string
    linkedServiceID: string
  }
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [certificateSearch, setCertificateSearch] = useState<string>('')
  let [certificateSection, setCertificateSection] = useState<string>('')
  let [certificateSort, setCertificateSort] = useState<SortOption>('date-new')
  let [downloading, setDownloading] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [existingFile, setExistingFile] = useState<string | undefined>('')

  let domain: Domain = config ? 'manage' : 'profile'

  let filteredCertificates = prepareCertificates(
    certificates,
    certificateSearch,
    certificateSort,
    null,
    null,
  )

  if (certificateSection) {
    filteredCertificates = filteredCertificates.filter(
      (cert) => cert.hourType === certificateSection,
    )
  }

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      courseCode: yup.string().required(),
      length: yup.number().not([0]).required(),
      hourType: yup.string().not(['unselected']).required(),
      courseType: yup.string().required(),
      dateCompleted: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      courseCode: '',
      length: 0,
      hourType: 'unselected',
      courseType: 'recorded',
      dateCompleted: null,
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post(`/api/v2/${domain}/external-certificate`, {
            ...(config || {}),
            name: values.name,
            courseCode: values.courseCode,
            length: values.length,
            hourType: values.hourType,
            courseType: values.courseType,
            dateCompleted: values.dateCompleted,
          })
          .then(({ data }) =>
            updateExternalCertificateImage(
              {
                id: data.id,
                imageType:
                  fileToUpload?.extension && fileToUpload?.extension === 'jpeg'
                    ? 'jpg'
                    : fileToUpload!.extension,
              },
              domain,
            ),
          )
          .then((response) =>
            axios.put(response, fileToUpload?.file, {
              headers: {
                'Cache-Control': 'max-age=2592000',
                'content-type': fileToUpload?.type,
              },
            }),
          )
          .then(() => dispatch(fetchCertificates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.message || 'Error Creating External Certificate',
            )
            dispatch(fetchCertificates())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put(`/api/v2/${domain}/external-certificate`, {
            id: values.id,
            name: values.name,
            courseCode: values.courseCode,
            length: values.length,
            hourType: values.hourType,
            courseType: values.courseType,
            dateCompleted: values.dateCompleted,
          })
          .then(() => {
            if (fileToUpload) {
              updateExternalCertificateImage(
                {
                  id: values.id,
                  imageType: fileToUpload.extension === 'jpeg' ? 'jpg' : 'png',
                },
                domain,
              ).then((response) =>
                axios.put(response, fileToUpload?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': fileToUpload?.type,
                  },
                }),
              )
            }
          })
          .then(() => dispatch(fetchCertificates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(
              err?.message || 'Error Updating External Certificate',
            )
            dispatch(fetchCertificates())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Certificate Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  let onEdit = (id: string) => {
    let externalEdit = certificates.find((ec) => ec.id === id)
    form.setFieldValue('id', externalEdit?.id)
    form.setFieldValue('name', externalEdit?.name)
    form.setFieldValue('length', externalEdit?.length)
    form.setFieldValue('hourType', externalEdit?.hourType)
    form.setFieldValue('courseType', externalEdit?.courseType)
    form.setFieldValue('courseCode', externalEdit?.courseCode)
    form.setFieldValue('dateCompleted', externalEdit?.dateCompleted)
    setExistingFile(externalEdit?.fileName)
    setFormOpen(true)
  }

  useEffect(() => {
    if (!formOpen) {
      setExistingFile('')
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [formOpen])

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
        <TextField
          sx={{ width: 240 }}
          variant="outlined"
          size="small"
          label="Search External Certificates"
          placeholder="Name"
          name="external-certificate-search"
          type="text"
          value={certificateSearch}
          onChange={(event: React.ChangeEvent) => {
            setCertificateSearch((event.target as HTMLInputElement).value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ width: 160 }}>
          <InputLabel size="small">Hour Type</InputLabel>
          <Select
            value={certificateSection}
            label="Hour Type"
            variant="outlined"
            size="small"
            onChange={(event) => setCertificateSection(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="airwayRespiratoryVentilation">
              Airway/Respiratory/Ventilation
            </MenuItem>
            <MenuItem value="medical">Medical</MenuItem>
            <MenuItem value="trauma">Trauma</MenuItem>
            <MenuItem value="operations">Operations</MenuItem>
            <MenuItem value="criticalCare">Critical Care</MenuItem>
            <MenuItem value="obgyn">OBGYN</MenuItem>
            <MenuItem value="pediatricsNeonatal">Pediatrics/Neonatal</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ width: 160 }}>
          <InputLabel size="small">Sort</InputLabel>
          <Select
            value={certificateSort}
            label="Sort"
            variant="outlined"
            size="small"
            onChange={(event) =>
              setCertificateSort(event.target.value as SortOption)
            }
          >
            <MenuItem value={'date-new'}>Date: Newest</MenuItem>
            <MenuItem value={'date-old'}>Date: Oldest</MenuItem>
            <MenuItem value={'name-az'}>Name: A-Z</MenuItem>
            <MenuItem value={'name-za'}>Name: Z-A</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Create
        </Button>
      </Box>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 15,
          marginBottom: 15,
          overflowY: 'scroll',
          maxHeight: 500,
        }}
      >
        {filteredCertificates.length ? (
          filteredCertificates.map((certificate) => (
            <Card
              key={certificate.id}
              variant="outlined"
              sx={{
                p: 2,
                width: 280,
                flex: '0 0 auto',
                height: 'fit-content',
              }}
              className="text-center"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ p: 1, mr: -0.0, display: 'inline', height: '40px' }}>
                  <CertificateIcon color="primary" sx={{ mb: 0.5 }} />
                </Box>
                <Typography variant="h6">{certificate.name}</Typography>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    sx={{ ml: '6px' }}
                    onClick={() => onEdit(certificate.id)}
                  >
                    <EditIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography color="text.secondary" variant="subtitle2">
                {certificate.courseCode}
              </Typography>
              <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                Completed{' '}
                {dayjs(certificate.dateCompleted).format('MM/DD/YYYY')}
              </div>
              {downloading === certificate.id ? (
                <CircularProgress size="24px" />
              ) : (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PdfIcon />}
                    disabled={!certificate.fileName}
                    onClick={() => {
                      setDownloading(certificate.id)
                      getCertificate(certificate.id, 'external', domain)
                        .then((response) =>
                          axios
                            .get(response, { responseType: 'blob' })
                            .catch(() => {
                              // eslint-disable-next-line fp/no-throw, no-throw-literal
                              throw 'File Not Found'
                            }),
                        )
                        .then(pluckData)
                        .then((response: Blob) => {
                          let extension = 'pdf'
                          if (response.type === 'image/png') {
                            extension = 'png'
                          } else if (response.type === 'image/jpeg') {
                            extension = 'jpeg'
                          }
                          downloadFile(
                            response,
                            `${certificate.name || certificate.id}.${extension}`,
                          )
                        })
                        .then(() => setDownloading(''))
                        .catch((err) => {
                          setDownloading('')
                          setErrorMessage(
                            `PDF Download Failed: ${err.response?.data || err}`,
                          )
                        })
                    }}
                  >
                    Download
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleting(`delete-${certificate.id}`)}
                  >
                    Delete
                  </Button>
                  <ConfirmAction
                    executeAction={() =>
                      deleteExternalCertificate(certificate.id).then(() =>
                        dispatch(fetchCertificates()),
                      )
                    }
                    open={deleting}
                    setOpen={setDeleting}
                    openText={`delete-${certificate.id}`}
                    title="Confirm Deletion"
                    text={`Are you sure you want to delete ${certificate.name}?`}
                    onFailure={(err) =>
                      setErrorMessage(
                        err.message || 'Error Deleting Certificate',
                      )
                    }
                  />
                </Box>
              )}
            </Card>
          ))
        ) : (
          <Typography color="text.secondary">
            There are no certificates matching these filters.
          </Typography>
        )}
      </div>
      <Dialog open={formOpen} onClose={() => setFormOpen(false)}>
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <form onSubmit={form.handleSubmit}>
            <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
              External Certificate
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 2,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
                label="Name"
                name="name"
                type="text"
                fullWidth
                value={form.values.name}
                onChange={form.handleChange}
                error={form.touched.name && !!form.errors.name}
              />
              <TextField
                variant="outlined"
                size="small"
                label="Length"
                name="length"
                type="number"
                fullWidth
                value={form.values.length}
                onChange={form.handleChange}
                error={form.touched.length && !!form.errors.length}
              />
              <TextField
                variant="outlined"
                size="small"
                label="Course Code"
                name="courseCode"
                type="text"
                fullWidth
                value={form.values.courseCode}
                onChange={form.handleChange}
                error={form.touched.courseCode && !!form.errors.courseCode}
              />
              <div className="flex justify-center">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date Completed"
                    value={dayjs(form.values.dateCompleted)}
                    onChange={(val: any) =>
                      form.setFieldValue(
                        'dateCompleted',
                        dayjs(val).format('MM/DD/YYYY'),
                      )
                    }
                  />
                </LocalizationProvider>
              </div>
              <FormControl fullWidth>
                <InputLabel>Hour Type</InputLabel>
                <Select
                  name="hourType"
                  value={form.values.hourType}
                  label="Hour Type"
                  onChange={form.handleChange}
                  variant="outlined"
                  size="small"
                  error={form.touched.hourType && !!form.errors.hourType}
                >
                  <MenuItem value="unselected">Unselected</MenuItem>
                  {[
                    'Airway Respiratory Ventilation',
                    'Medical',
                    'Trauma',
                    'Operations',
                    'Critical Care',
                    'OBGYN',
                    'Pediatrics Neonatal',
                    'Other',
                  ].map((t) => (
                    <MenuItem key={t} value={t.replace(/ /g, '').toLowerCase()}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel sx={{ m: 0, textSizeAdjust: 20 }}>
                  Course Type
                </FormLabel>
                <RadioGroup
                  row
                  name="courseType"
                  value={form.values.courseType}
                  onChange={form.handleChange}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    value="recorded"
                    control={<Radio />}
                    label="Recorded"
                  />
                  <FormControlLabel
                    sx={{ m: 0 }}
                    value="live"
                    control={<Radio />}
                    label="Live"
                  />
                </RadioGroup>
              </FormControl>
              <Box>
                <label style={{ marginTop: -8 }}>
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                  >
                    <input
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleCapture(e)}
                    />
                    {!form.values.id || !existingFile ? 'Select' : 'Overwrite'}{' '}
                    File
                  </Button>
                </label>
                {fileToUpload && (
                  <Typography variant="subtitle1">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                )}
                {existingFile && (
                  <Typography color="text.secondary" variant="subtitle2">
                    *The existing certificate file will only be overwritten upon
                    submitting this form with a new image uploaded.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
              {saving ? (
                <CircularProgress className="m-auto" size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                    disabled={!existingFile && !fileToUpload}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setFormOpen(false)}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </Paper>
      </Dialog>
    </>
  )
}
